<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage Jobs</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      :new-to="{ name: 'job.create', params: { id: 0 } }"
      new-button-text="New Job"
    >
    </o-list-toolbar>

    <b-table
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :tbody-tr-class="rowClass"
      :sort-desc="list_options.order == 'desc'"
    >
      <template slot="cell(job_date)" slot-scope="{ index, item }">
        <div :class="{onhold: item.on_hold}">
        <o-inline-widget>

          <date-display  slot="display"
            :value="item.job_date"
            format="DD/MM/YYYY ddd"
          ></date-display>

          <o-date-time-input
            slot="edit"
            :value="item.job_date"
            :id="item.id"
            store-name="Job"
            field-name="job_date"
            hide-time
            no-buttons
            inline
          ></o-date-time-input>
        </o-inline-widget>
        </div>
      </template>

      <template slot="cell(concrete_time)" slot-scope="{ index, item }">
        <o-inline-widget>
          <date-display
            slot="display"
            :value="toDateTime(item.job_date, item.concrete_time)"
            format="hh:mm a"
            default-value="[Set]"
          ></date-display>
          <o-date-time-input
            slot="edit"
            :value="item.concrete_time"
            :id="item.id"
            store-name="Job"
            field-name="concrete_time"
            hide-date
            no-buttons
            inline
          ></o-date-time-input>
        </o-inline-widget>
      </template>
      <template slot="cell(job_type)" slot-scope="{ index, item }">
        <o-inline-widget>
          <lookup-display
            style="float: right"
            slot="display"
            :list="optionCodeJobTypeItemsById"
            :value="item.job_type"
            display-field="description"
            default-value="-"
          ></lookup-display>
          <o-select-input
            slot="edit"
            :value="item.job_type"
            :id="item.id"
            store-name="Job"
            field-name="job_type"
            :items="optionCodeJobTypeItems"
          ></o-select-input>
        </o-inline-widget>
      </template>
      <template slot="cell(customer)" slot-scope="{ index, item }">
        <o-inline-widget>
          <lookup-display
            slot="display"
            :list="optionCustomerItemsById"
            :value="item.customer"
            display-field="name"
            default-value="-"
          ></lookup-display>
          <o-select-input
            slot="edit"
            :value="item.customer"
            :id="item.id"
            store-name="Job"
            field-name="customer"
            :items="optionCustomerItems"
            text-field="name"
          ></o-select-input>
        </o-inline-widget>
      </template>
      <template slot="cell(address)" slot-scope="{ index, item }">
        <o-inline-widget>
          <default-display
            slot="display"
            :value="item.address"
          ></default-display>
          <o-text-input
            slot="edit"
            :value="item.address"
            :id="item.id"
            store-name="Job"
            field-name="address"
          ></o-text-input>
        </o-inline-widget>
      </template>
      <template slot="cell(comments)" slot-scope="{ index, item }">
        <o-inline-widget>
          <default-display
            slot="display"
            :value="item.comments"
          ></default-display>
          <o-textarea-input
            slot="edit"
            :value="item.comments"
            :id="item.id"
            store-name="Job"
            field-name="comments"
          ></o-textarea-input>
        </o-inline-widget>
      </template>
      <template slot="cell(invoice_date)" slot-scope="{ index, item }">
        <o-inline-widget>
          <date-display
            slot="display"
            :value="item.invoice_date"
            format="DD/MM/YYYY"
          ></date-display>
          <o-date-time-input
            slot="edit"
            :value="item.invoice_date"
            :id="item.id"
            store-name="Job"
            field-name="invoice_date"
            hide-time
            no-buttons
          ></o-date-time-input>
        </o-inline-widget>
      </template>
      <template slot="cell(supervisor)" slot-scope="{ index, item }">
        <o-inline-widget>
          <lookup-display
            slot="display"
            :list="optionSupervisorItemsById"
            :value="item.supervisor"
            display-field="description"
            default-value="-"
          ></lookup-display>
          <o-select-input
            slot="edit"
            :value="item.supervisor"
            :id="item.id"
            store-name="Job"
            field-name="supervisor"
            :items="optionSupervisorItems"
          ></o-select-input>
        </o-inline-widget>
      </template>
      <template slot="cell(cubic_metres)" slot-scope="{ index, item }">
        <o-inline-widget>
          <number-display
            style="float: right"
            slot="display"
            :value="item.cubic_metres"
            :decimals="2"
          />
          <o-text-input
            slot="edit"
            :value="item.cubic_metres"
            :id="item.id"
            store-name="Job"
            field-name="cubic_metres"
            type="number"
            step="0.01"
          ></o-text-input>
        </o-inline-widget>
      </template>
      <template slot="cell(id)" slot-scope="{ index, item }">
        <router-link :to="{ name: 'job.edit', params: { id: item.id } }">
          <number-display :value="item.id" :decimals="0" />
        </router-link>
      </template>
      <template slot="cell(supplier)" slot-scope="{ index, item }">
        <o-inline-widget>
          <lookup-display
            slot="display"
            :list="optionSupplierItemsById"
            :value="item.supplier"
            display-field="description"
            default-value="-"
          ></lookup-display>
          <o-select-input
            slot="edit"
            :value="item.supplier"
            :id="item.id"
            store-name="Job"
            field-name="supplier"
            :items="optionSupplierItems"
          ></o-select-input>
        </o-inline-widget>
      </template>
      <template slot="cell(suburb)" slot-scope="{ index, item }">
        <o-inline-widget>
          <default-display
            slot="display"
            :value="item.suburb"
          ></default-display>
          <o-text-input
            slot="edit"
            :value="item.suburb"
            :id="item.id"
            store-name="Job"
            field-name="suburb"
          ></o-text-input>
        </o-inline-widget>
      </template>
      <template slot="cell(pump)" slot-scope="{ index, item }">
        <o-inline-widget>
          <lookup-display
            slot="display"
            :list="optionPumpItemsById"
            :value="item.pump"
            display-field="description"
            default-value="-"
          ></lookup-display>
          <o-select-input
            slot="edit"
            :value="item.pump"
            :id="item.id"
            store-name="Job"
            field-name="pump"
            :items="optionPumpItems"
          ></o-select-input>
        </o-inline-widget>
      </template><template slot="cell(linehand)" slot-scope="{ index, item }">
        <o-inline-widget>
          <lookup-display
            slot="display"
            :list="optionEmployeeItemsById"
            :value="item.linehand"
            display-field="name"
            default-value="-"
          ></lookup-display>
          <o-select-input
            slot="edit"
            :value="item.linehand"
            :id="item.id"
            store-name="Job"
            field-name="linehand"
            text-field="name"
            :items="optionEmployeeItems"
          ></o-select-input>
        </o-inline-widget>
      </template>
      <template slot="cell(confirmed)" slot-scope="{ index, item }">
        <o-inline-widget>
          <boolean-display
            slot="display"
            :value="item.confirmed"
          ></boolean-display>
          <o-check-box-input
            slot="edit"
            :value="item.confirmed"
            :id="item.id"
            store-name="Job"
            field-name="confirmed"
          ></o-check-box-input>
        </o-inline-widget>
      </template>
      <template slot="cell(site_contact__name)" slot-scope="{ index, item }">
        <o-inline-widget>
          <lookup-display
            slot="display"
            :list="optionSiteContactItemsById"
            :value="item.site_contact"
            display-field="name"
            default-value="-"
          ></lookup-display>
          <lookup-display
            slot="display"
            style="margin-left: 5px"
            :list="optionSiteContactItemsById"
            :value="item.site_contact"
            display-field="phone_number"
            default-value="-"
          ></lookup-display>
          <o-select-input
            slot="edit"
            :value="item.site_contact"
            :id="item.id"
            store-name="Job"
            field-name="site_contact"
            :items="optionSiteContactItems"
            text-field="name"
          ></o-select-input>
        </o-inline-widget>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'job.create' }"
          >create a new item</router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
} from "../../../vue-ocom/mixins";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";
import OTextInput from "../../../vue-ocom/components/forms/fields/vuex/OTextInput";
import OCheckBoxInput from "../../../vue-ocom/components/forms/fields/vuex/OCheckBoxInput";
import ODateTimeInput from "../../../vue-ocom/components/forms/fields/vuex/ODateTimeInput";
import OSelectInput from "../../../vue-ocom/components/forms/fields/vuex/OSelectInput";
import OInlineWidget from "../../../vue-ocom/components/table/OInlineWidget";

export default {
  components: {
    OInlineWidget,
    OSelectInput,
    ODateTimeInput,
    OCheckBoxInput,
    OTextInput,
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("Job", "job"),
    CreateGroupPermissionMixin("Job"),
    CreateCodeTableFetchMixin([
      "Customer",
      "Pump",
      "SiteContact",
      "Supervisor",
      "CodeJobType",
      "Supplier",
      "Employee",
    ]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(schema.Job, {}) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.Job),

      filter_list: [
        {
          name: "Current",
          code: "current",
          options: { offset: 0, sort: "job_date" },
        },
        {
          name: "On Hold",
          code: "onhold",
          options: { offset: 0, sort: "job_date" },
        },
        {
          name: "Active",
          code: "active",
          options: { offset: 0, sort: "job_date" },
        },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "job_date" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "job_date" } },

        {
          name: "Not Invoiced",
          code: "not_invoiced",
          options: { offset: 0, sort: "job_date" },
        },
        {
          name: "Invoiced",
          code: "invoiced",
          options: { offset: 0, sort: "job_date" },
        },
      ],
    };
  },
  methods: {
    rowClass(item, type) {
      if (!item) return;

      var moment = require("moment");

      var today = moment().startOf("day");
      var days = moment(item.job_date).diff(today, "d");

      if (!_.isUndefined(days)) {
        var result = ["row-" + days];

        return result;
      } else {
        return [];
      }
    },
    toDateTime(datePart, timepart) {
      if (!datePart) {
        var moment = require("moment");

        datePart = moment().toISOString(); // use today
      }

      if (!timepart) {
        return null; // there is no value!
      }

      var the_date = (datePart + "").split("T");

      var result = the_date[0] + "T" + timepart;

      return result;
    },
  },
  computed: {
    options() {
      return {
        Customer: {
          options: this.optionCustomerItems,
          textField: "name",
          valueField: "id",
        },
        Pump: {
          options: this.optionPumpItems,
          textField: "description",
          valueField: "id",
        },
        SiteContact: {
          options: this.optionSiteContactItems,
          textField: "name",
          valueField: "id",
        },
        Supervisor: {
          options: this.optionSupervisorItems,
          textField: "description",
          valueField: "id",
        },
        CodeJobType: {
          options: this.optionCodeJobTypeItems,
          textField: "description",
          valueField: "id",
        },
        Supplier: {
          options: this.optionSupplierItems,
          textField: "description",
          valueField: "id",
        },
      };
    },
    fields() {
      return this.filterTableColumns(this.schema, [
        fieldToTableColumn(this.schema, "job_date", { label: "Date" }),
        fieldToTableColumn(this.schema, "id", { label: "Job Number" }),
        fieldToTableColumn(this.schema, "customer"),
        fieldToTableColumn(this.schema, "address"),
        fieldToTableColumn(this.schema, "suburb"),
        fieldToTableColumn(this.schema, "cubic_metres", { label: "m3" }),
        fieldToTableColumn(this.schema, "job_type"),
        fieldToTableColumn(this.schema, "pump"),
        fieldToTableColumn(this.schema, "linehand"),
        fieldToTableColumn(this.schema, "confirmed"),
        fieldToTableColumn(this.schema, "concrete_time"),
        fieldToTableColumn(this.schema, "supplier"),

        // fieldToTableColumn(this.schema, "site_contact", {
        //   key: "site_contact__name",
        // }),

        fieldToTableColumn(this.schema, "comments"),

        fieldToTableColumn(this.schema, "invoice_date"),
      ]);
    },
  },
};
</script>
<style>
.row--4 {
  background-color: #f9f9f9;
}
.row--3 {
  background-color: #efefef;
}
.row--2 {
  background-color: #e3e3e3;
}
.row--1 {
  background-color: #c1cdcd;
}
.row-0 {
  background-color: #c0ff97;
}
.row-1 {
  background-color: #edef85;
}
.row-2 {
  background-color: #fbb;
}
.row-3 {
  background-color: #bbc2ff;
}
.onhold {
  text-decoration: line-through;
  color: silver;
}
</style>
